import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SEO from '../../components/SEO';
import { Header, HomeSection, FeaturesSection, PricingSection, ContactSection, Footer } from './components';
import homePageStyles from './HomePage.module.scss';

export const sectionToRoute = {
  'home-section': '/',
  'features-section': '/features',
  'pricing-section': '/pricing',
  'contact-section': '/contact',
};
const sectionIds = Object.keys(sectionToRoute);

function useSectionUrlSync(sectionIds, sectionToRoute) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentSectionRef = useRef(null);
  const ratiosRef = useRef({});
  const debounceRef = useRef();
  const HYSTERESIS = 0.1; // 10% more visible required to switch

  useEffect(() => {
    const handleIntersect = (entries) => {
      entries.forEach(entry => {
        ratiosRef.current[entry.target.id] = entry.intersectionRatio;
      });
      let maxId = null;
      let maxRatio = 0.5;
      for (const id of sectionIds) {
        const ratio = ratiosRef.current[id] || 0;
        if (ratio > maxRatio) {
          maxRatio = ratio;
          maxId = id;
        }
      }
      const currentId = currentSectionRef.current;
      const currentRatio = currentId ? (ratiosRef.current[currentId] || 0) : 0;
      if (
        maxId &&
        (maxId !== currentId) &&
        (maxRatio > currentRatio + HYSTERESIS || currentRatio < 0.5)
      ) {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          currentSectionRef.current = maxId;
          const route = sectionToRoute[maxId];
          if (route && location.pathname !== route) {
            navigate(route, { replace: true });
          }
        }, 80);
      }
    };

    const observer = new window.IntersectionObserver(handleIntersect, {
      threshold: Array.from({length: 21}, (_, i) => i * 0.05),
    });

    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
      clearTimeout(debounceRef.current);
      ratiosRef.current = {};
    };
  }, [navigate, location.pathname, sectionIds, sectionToRoute]);
}

function HomePage({ scrollTo }) {
  const [isNavOpen, setIsNavOpen] = useState(false);

  const toggleNav = useCallback(() => {
    setIsNavOpen(prev => !prev);
  }, []);

  const closeNav = useCallback(() => {
    if (isNavOpen) {
       setIsNavOpen(false);
    }
  }, [isNavOpen]);

  useSectionUrlSync(sectionIds, sectionToRoute);

  useEffect(() => {
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [scrollTo]);

  return (
    <>
      <SEO 
        title="mobiyo - Mobile App Review Management & AI Response Generation"
        description="mobiyo collects customer reviews for your mobile application, analyzes comments and generates responses with AI. Manage your app reviews efficiently with our AI-powered platform."
        keywords="mobile app reviews, app review management, AI response generation, app store reviews, Google Play reviews, App Store reviews, review analytics"
        canonical="https://mobiyo.app/"
      />
      <div className={homePageStyles.container}>
        <Header onHamburgerClick={toggleNav} isNavOpen={isNavOpen} closeNav={closeNav} />
        <main className={homePageStyles.main}>
          <HomeSection />
          <FeaturesSection />
          <PricingSection />
          <ContactSection />
        </main>
        <Footer />
      </div>
    </>
  );
}

export { HomePage };
